import React from 'react';
import {
  Box,
  Text,
  Button,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  TableToolbar,
  TableToolbarActions,
  RefreshIcon,
  TablePagination,
  QuickFilterGroup,
  ListView,
  ListViewFilters,
  QuickFilter,
} from '@razorpay/blade/components';
import { usePagination } from 'hooks/usePagination';
import { formatPaginationRange } from './utils';
import { useFilters } from 'hooks/useFilters';
import employeeBankAccountApis, {
  BankAccountApprovalStatus,
  GET_BANK_ACCOUNT_REQUESTS,
  GetBankAccountRequestsQueryParams,
} from 'api/employeeBankAccount';
import { useQuery } from 'react-query';
import { dateFormats } from 'utils/Dates';
import { getFormattedDateValue } from 'utils/Dates';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { EmptyView } from './EmptyView';
import { bankAccountApprovalStatus } from 'components/EmployeeDetails/BankAccountDetails/data';

const bankApprovalStatuses: Record<NonNullable<BankAccountApprovalStatus>, string> = {
  approved: 'Approved',
  rejected: 'Rejected',
  pending: 'Pending',
  auto_approved: 'Approved',
} as const;

const filtersMap = {
  status: 'status',
} as const;

export const Completed = () => {
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });
  const { currentFilters, applyFilter } = useFilters({
    filtersMap,
  });

  const queryParams: GetBankAccountRequestsQueryParams = {
    approval_statuses:
      !currentFilters.status || currentFilters.status === 'all'
        ? [bankAccountApprovalStatus.approved, bankAccountApprovalStatus.rejected]
        : [currentFilters.status],
    count: itemsPerPage,
    skip: currentPage * itemsPerPage,
  };

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: [GET_BANK_ACCOUNT_REQUESTS, queryParams],
    queryFn: () => {
      return employeeBankAccountApis.getBankAccountRequests(queryParams);
    },
  });

  const completedRequests = data?.data?.items ?? [];

  if (isError) {
    return (
      <Box marginTop="spacing.6">
        <ErrorView onRefetch={refetch} />
      </Box>
    );
  }

  return (
    <Box marginTop="spacing.6">
      <ListView>
        <ListViewFilters
          quickFilters={
            <QuickFilterGroup
              onChange={({ values }) => applyFilter(filtersMap.status, values[0])}
              selectionType="single"
              value={currentFilters.status ?? 'all'}>
              <QuickFilter title="All Requests" value="all" />
              <QuickFilter title="Approved" value="approved" />
              <QuickFilter title="Rejected" value="rejected" />
            </QuickFilterGroup>
          }
          children={undefined}
        />

        <Table
          isLoading={isLoading}
          isRefreshing={isFetching}
          data={{
            nodes: completedRequests.map((item) => ({
              id: item.id,
              data: {
                requestDate: item.created_at ? new Date(item.created_at) : null,
                employee: item.employee_name,
                beneficiary: item.beneficiary_name,
                status: item.approval_status,
              },
            })),
          }}
          pagination={
            completedRequests.length > 0 ? (
              <TablePagination
                currentPage={currentPage}
                paginationType="server"
                showPageSizePicker={true}
                onPageChange={({ page }) => {
                  setCurrentPage(page);
                }}
                onPageSizeChange={({ pageSize }) => {
                  setItemsPerPage(pageSize);
                  setCurrentPage(0);
                }}
                totalItemCount={data?.data?.total_count ?? 0}
              />
            ) : undefined
          }
          toolbar={
            <TableToolbar
              title={
                completedRequests.length > 0
                  ? `Showing ${formatPaginationRange({
                      totalItems: data?.data?.count ?? 0,
                      currentPage,
                      itemsPerPage,
                    })} Completed Requests`
                  : ''
              }>
              <TableToolbarActions>
                <Button
                  accessibilityLabel="Refresh Bank Account Requests"
                  icon={RefreshIcon}
                  variant={'tertiary'}
                  onClick={refetch}
                />
              </TableToolbarActions>
            </TableToolbar>
          }>
          {(nodes) =>
            completedRequests.length > 0 ? (
              <>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>
                      <Text size="medium" weight="semibold">
                        Request Date
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text size="medium" weight="semibold">
                        Employee Name
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text size="medium" weight="semibold">
                        Account Holder Name
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Text size="medium" weight="semibold">
                        Status
                      </Text>
                    </TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {nodes.map((item) => (
                    <TableRow key={item.id} item={item}>
                      <TableCell>
                        <Text size="medium" weight="regular">
                          {getFormattedDateValue({
                            date: item.data.requestDate,
                            formatString: dateFormats.dateShortMonthCommaYear,
                          })}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text size="medium" weight="regular">
                          {item.data.employee}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text size="medium" weight="regular">
                          {item.data.beneficiary ?? '--'}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Badge color={item.data.status === 'rejected' ? 'negative' : 'positive'}>
                          {bankApprovalStatuses[item.data.status]}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </>
            ) : (
              <EmptyView />
            )
          }
        </Table>
      </ListView>
    </Box>
  );
};
